import PropTypes from 'prop-types';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { format, sub } from 'date-fns';
import {
  Alert,
  Box,
  Card,
  CardHeader,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { endpoints } from '../../../config';
import { authApiCall } from '../../../services/http';
import { setAuthUser, setAuthToken } from '../../../redux/reducers/authSlice';
import Iconify from '../../../components/Iconify';

// Mismas features que el drill-in de usuarios (UserMetricsDialog). `completion` se omite
// a propósito — la guía del back dice que no aporta info útil.
const SESSION_TYPES = ['chat', 'template_agent', 'blocknote_agent', 'opportunity_finder', 'report'];
const RANGE_OPTIONS = [
  { value: 7, label: 'Últimos 7 días' },
  { value: 14, label: 'Últimos 14 días' },
  { value: 30, label: 'Últimos 30 días' },
];

const formatUsd = (n) => `$${(Number(n) || 0).toFixed(4)}`;
const formatInt = (n) => (Number(n) || 0).toLocaleString('es-ES');

// `createdAt` aún no lo devuelve el endpoint (pendiente en el back). Cuando llegue,
// esta columna se rellena sola — formatDateTime ya cae a "—" si no viene.
const formatDateTime = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('es-ES');
};

// Respuesta de /nlp-api/token-usage: { filters, totalCost, currency, stages: [...] }.
// `stages` es una lista plana de llamadas a modelos de IA — una por paso del pipeline.
// `userId` es solo un filtro de query; sin pasarlo, `stages` trae las de todos los usuarios.
// Cada stage: { userId, sessionType, stage, provider, model, inputTokens, outputTokens, cost }.
// `createdAt` está pendiente de que lo añada el back; `sessionId` no se expone.
function extractStages(data) {
  if (Array.isArray(data?.stages)) return data.stages;
  if (Array.isArray(data)) return data;
  return [];
}

// Agrega un conjunto de llamadas por un campo (`stage` o `model`): nº de llamadas, tokens
// y coste total. Ordenado por coste desc — la primera fila es donde más se gasta.
// `collectField` opcional: recoge los valores distintos de ese campo por grupo (p. ej.
// los modelos usados en cada stage).
function aggregateBy(rows, field, collectField) {
  const map = new Map();
  rows.forEach((r) => {
    const k = r[field] || '—';
    if (!map.has(k)) {
      map.set(k, { key: k, calls: 0, inputTokens: 0, outputTokens: 0, cost: 0, values: new Set() });
    }
    const a = map.get(k);
    a.calls += 1;
    a.inputTokens += r.inputTokens;
    a.outputTokens += r.outputTokens;
    a.cost += r.cost;
    if (collectField && r[collectField]) a.values.add(r[collectField]);
  });
  return [...map.values()].map((a) => ({ ...a, values: [...a.values] })).sort((a, b) => b.cost - a.cost);
}

// Tabla agregada del desplegable de `report` — una por stage y otra por modelo.
// `valuesLabel` opcional añade una columna con los valores distintos recogidos (modelos).
function AggregateTable({ title, keyLabel, valuesLabel, rows }) {
  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="caption" color="text.secondary">
        {title}
      </Typography>
      <Table size="small" sx={{ mt: 0.5 }}>
        <TableHead>
          <TableRow>
            <TableCell>{keyLabel}</TableCell>
            {valuesLabel && <TableCell>{valuesLabel}</TableCell>}
            <TableCell align="right">Llamadas</TableCell>
            <TableCell align="right">Tokens (entrada / salida)</TableCell>
            <TableCell align="right">Coste</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.key}>
              <TableCell sx={{ fontFamily: 'monospace' }}>{r.key}</TableCell>
              {valuesLabel && (
                <TableCell sx={{ fontFamily: 'monospace' }}>
                  {r.values && r.values.length ? r.values.join(', ') : '—'}
                </TableCell>
              )}
              <TableCell align="right">{r.calls}</TableCell>
              <TableCell align="right">
                {formatInt(r.inputTokens)} / {formatInt(r.outputTokens)}
              </TableCell>
              <TableCell align="right">{formatUsd(r.cost)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

AggregateTable.propTypes = {
  title: PropTypes.string.isRequired,
  keyLabel: PropTypes.string.isRequired,
  valuesLabel: PropTypes.string,
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default function TokenUsageByUserTable() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const authToken = useSelector((state) => state.auth.authToken);

  const [sessionType, setSessionType] = useState('chat');
  const [rangeDays, setRangeDays] = useState(7);
  const [stages, setStages] = useState([]);
  const [apiTotalCost, setApiTotalCost] = useState(null);
  const [range, setRange] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedKeys, setExpandedKeys] = useState(() => new Set());

  useEffect(() => {
    let ignore = false;
    setLoading(true);
    setError(null);
    setExpandedKeys(new Set());

    const to = format(new Date(), 'yyyy-MM-dd');
    const from = format(sub(new Date(), { days: rangeDays - 1 }), 'yyyy-MM-dd');

    (async () => {
      try {
        const res = await authApiCall({
          method: 'GET',
          endpoint: endpoints.tokenUsage,
          path: `?sessionType=${sessionType}&from=${from}&to=${to}`,
          navigate,
          dispatch,
          setAuthUser,
          setAuthToken,
          authToken,
          redirectRoute: null,
        });
        if (ignore) return;
        setStages(extractStages(res?.data));
        setApiTotalCost(res?.data && !Array.isArray(res.data) ? Number(res.data.totalCost) : null);
        setRange({ from, to });
      } catch (e) {
        if (ignore) return;
        setError(e?.response?.data?.error || e?.message || 'Error cargando el uso de IA');
        setStages([]);
        setApiTotalCost(null);
      } finally {
        if (!ignore) setLoading(false);
      }
    })();

    return () => {
      ignore = true;
    };
  }, [sessionType, rangeDays, authToken, dispatch, navigate]);

  // Agrupa las llamadas por `userId`. Cada fila del desplegable es una llamada a un modelo.
  const groups = useMemo(() => {
    const byUser = new Map();
    stages.forEach((s) => {
      const userId = s?.userId ?? null;
      const key = userId == null ? '—' : String(userId);
      if (!byUser.has(key)) byUser.set(key, { key, userId, calls: 0, cost: 0, rows: [] });
      const g = byUser.get(key);
      const row = {
        stage: s?.stage || '—',
        model: s?.model || '—',
        inputTokens: Number(s?.inputTokens) || 0,
        outputTokens: Number(s?.outputTokens) || 0,
        cost: Number(s?.cost) || 0,
        createdAt: s?.createdAt ?? null,
      };
      g.calls += 1;
      g.cost += row.cost;
      g.rows.push(row);
    });
    return [...byUser.values()]
      .map((g) => ({
        ...g,
        // Más recientes primero cuando llegue `createdAt`; sin fecha, mantiene el orden del back.
        rows: [...g.rows].sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)),
        // Agregados para el desplegable de `report`: dónde se concentra el coste.
        byStage: aggregateBy(g.rows, 'stage', 'model'),
        byModel: aggregateBy(g.rows, 'model'),
      }))
      .sort((a, b) => b.cost - a.cost);
  }, [stages]);

  const computedTotal = useMemo(() => stages.reduce((acc, s) => acc + (Number(s?.cost) || 0), 0), [stages]);
  const headlineTotal = apiTotalCost != null && !Number.isNaN(apiTotalCost) ? apiTotalCost : computedTotal;

  const toggle = (key) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  let subheader = '';
  if (loading) {
    subheader = 'Cargando…';
  } else if (range) {
    subheader =
      `Rango ${range.from} → ${range.to} · ` +
      `${groups.length} usuario${groups.length === 1 ? '' : 's'} · ` +
      `${stages.length} llamada${stages.length === 1 ? '' : 's'} · ` +
      `Total ${formatUsd(headlineTotal)}`;
  }

  return (
    <Card>
      <CardHeader
        title="Uso de IA por usuario"
        subheader={subheader}
        subheaderTypographyProps={{ variant: 'caption' }}
        action={
          <Stack direction="row" spacing={1}>
            <TextField
              select
              size="small"
              label="Feature"
              value={sessionType}
              onChange={(e) => setSessionType(e.target.value)}
              sx={{ minWidth: 180 }}
            >
              {SESSION_TYPES.map((st) => (
                <MenuItem key={st} value={st} sx={{ fontFamily: 'monospace' }}>
                  {st}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label="Rango"
              value={rangeDays}
              onChange={(e) => setRangeDays(Number(e.target.value))}
              sx={{ minWidth: 150 }}
            >
              {RANGE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        }
      />
      <Divider />

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={40} />
        </Box>
      )}

      {!loading && error && (
        <Box sx={{ p: 3 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      {!loading && !error && groups.length === 0 && (
        <Box sx={{ p: 3 }}>
          <Typography variant="body2" color="text.secondary">
            No hay uso de <strong>{sessionType}</strong> en este rango.
          </Typography>
        </Box>
      )}

      {!loading && !error && groups.length > 0 && (
        <>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 48 }} />
                  <TableCell>Usuario (userId)</TableCell>
                  <TableCell align="right">Llamadas</TableCell>
                  <TableCell align="right">Coste total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {groups.map((g) => {
                  const open = expandedKeys.has(g.key);
                  return (
                    <Fragment key={g.key}>
                      <TableRow
                        hover
                        onClick={() => toggle(g.key)}
                        sx={{ cursor: 'pointer', '& > *': { borderBottom: 'unset' } }}
                      >
                        <TableCell>
                          <IconButton size="small">
                            <Iconify
                              icon={open ? 'eva:arrow-ios-downward-fill' : 'eva:arrow-ios-forward-fill'}
                              width={18}
                              height={18}
                            />
                          </IconButton>
                        </TableCell>
                        <TableCell sx={{ fontFamily: 'monospace' }}>{g.userId ?? '— sin userId —'}</TableCell>
                        <TableCell align="right">{g.calls}</TableCell>
                        <TableCell align="right">{formatUsd(g.cost)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ py: 0 }} colSpan={4}>
                          <Collapse in={open} timeout="auto" unmountOnExit>
                            <Box sx={{ m: 1, ml: 6 }}>
                              {sessionType === 'report' ? (
                                <>
                                  <AggregateTable
                                    title="Coste por stage"
                                    keyLabel="Stage"
                                    valuesLabel="Modelos"
                                    rows={g.byStage}
                                  />
                                  <AggregateTable title="Coste por modelo" keyLabel="Modelo" rows={g.byModel} />
                                </>
                              ) : (
                                <>
                                  <Typography variant="caption" color="text.secondary">
                                    Desglose por llamada
                                  </Typography>
                                  <Table size="small" sx={{ mt: 0.5 }}>
                                    <TableHead>
                                      <TableRow>
                                        <TableCell>Fecha</TableCell>
                                        <TableCell>Stage</TableCell>
                                        <TableCell>Modelo</TableCell>
                                        <TableCell align="right">Tokens (entrada / salida)</TableCell>
                                        <TableCell align="right">Coste</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {g.rows.map((r, idx) => (
                                        <TableRow key={`${g.key}-${idx}`}>
                                          <TableCell>{formatDateTime(r.createdAt)}</TableCell>
                                          <TableCell sx={{ fontFamily: 'monospace' }}>{r.stage}</TableCell>
                                          <TableCell sx={{ fontFamily: 'monospace' }}>{r.model}</TableCell>
                                          <TableCell align="right">
                                            {formatInt(r.inputTokens)} / {formatInt(r.outputTokens)}
                                          </TableCell>
                                          <TableCell align="right">{formatUsd(r.cost)}</TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </>
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ px: 3, py: 1.5 }}>
            <Typography variant="caption" color="text.disabled">
              {sessionType === 'report'
                ? 'Cada llamada es un paso (stage) del pipeline de IA. El desglose agrupa las llamadas por stage y por modelo para ver dónde se concentra el coste.'
                : 'Cada llamada es un paso (stage) del pipeline de IA, no una sesión. La columna «Fecha» se rellena cuando el back devuelva createdAt; hasta entonces aparece como «—».'}
            </Typography>
          </Box>
        </>
      )}
    </Card>
  );
}
